"use client";

import {
  WorkplaceRequestCategory,
  WorkplaceRequestLocationValue,
  WorkplaceRequestTypeElement
} from "@gettactic/api";
import { HookFormTextareaInput } from "@gettactic/components/src/form/TextareaInput";
import { HookFormTextInput } from "@gettactic/components/src/form/TextInput";
import { HookFormUnifiedSelectInput } from "@gettactic/components/src/form/UnifiedSelectInput";
import React from "react";
import type { FieldValues, UseFormReturn } from "react-hook-form";
import { DeskAutomationPreview } from "@/features/workplace-requests/components/DeskAutomationPreview";
import { DynamicFieldInput } from "./DynamicFieldInput";
import { packFormFields } from "./packFormFields";
import { RequestTypeHeader } from "./RequestTypeHeader";
import type { ReservationResourceContext } from "./ReservationResourceAvailability";

interface OfficeOption {
  id: string;
  name: string;
}

interface RequestFormStepProps {
  form: UseFormReturn<FieldValues>;
  type: WorkplaceRequestTypeElement;
  category?: WorkplaceRequestCategory;
  offices: OfficeOption[];
  /** When true, the office is fixed (e.g. dictated by a reservation): shown but read-only. */
  officeDisabled?: boolean;
  /** Returns to the type-picker step so the user can change the selected request type. */
  onChangeType?: () => void;
  /** Ticket-level location, so the automation preview can name the desk. */
  location?: WorkplaceRequestLocationValue | null;
  /** Who is filing the request, for rules that read the submitter. */
  submitterId?: string | null;
  /** The resource the request is filed against, so reservation fields can show its day. */
  reservationResource?: ReservationResourceContext | null;
}

export const RequestFormStep: React.FC<RequestFormStepProps> = ({
  form,
  type,
  category,
  offices,
  officeDisabled = false,
  onChangeType,
  location = null,
  submitterId = null,
  reservationResource = null
}) => {
  return (
    <div className="flex flex-col gap-5">
      <RequestTypeHeader
        type={type}
        category={category}
        onChangeType={onChangeType}
      />

      {/* Title and description are first-class on every request ticket, so they
          are shown regardless of the type's configured form fields — except when
          the type generates the title from its answers, or hides the
          description, which then carries none at all. */}
      {type.title_template ? null : (
        <HookFormTextInput
          form={form}
          name="title"
          label="Title"
          isRequired
          placeholder="Brief summary of your request"
        />
      )}

      {type.hide_description ? null : (
        <HookFormTextareaInput
          form={form}
          name="description"
          label="Description"
          placeholder="Add any details that will help resolve your request"
        />
      )}

      <HookFormUnifiedSelectInput
        form={form}
        name="office_id"
        label="Filing this for"
        items={offices.map((o) => ({ id: o.id, name: o.name }))}
        placeholder="Select a location"
        disabled={officeDisabled}
      />

      {packFormFields(type.fields ?? []).map((row) =>
        row.length === 2 ? (
          <div
            key={row[0].id}
            className="grid grid-cols-1 gap-5 sm:grid-cols-2"
          >
            {row.map((field) => (
              <DynamicFieldInput
                key={field.id}
                form={form}
                field={field}
                reservationResource={reservationResource}
              />
            ))}
          </div>
        ) : (
          <DynamicFieldInput
            key={row[0].id}
            form={form}
            field={row[0]}
            reservationResource={reservationResource}
          />
        )
      )}

      <DeskAutomationPreview
        form={form}
        type={type}
        location={location}
        submitterId={submitterId}
      />
    </div>
  );
};
